import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import {
	DateString,
	getCurrentDateString,
} from "seedgreen-shared/types/datestring.types";
import { useSeasons } from "seedgreen-shared/queries/season";
import { useCoolers } from "seedgreen-shared/queries/cooler";
import { useHarvestOverviewRanches } from "seedgreen-shared/queries/harvest-overview";
import { useBerriesRegions } from "@/views/Harvest/Berries/useBerriesRegions";

export const useBerriesHarvestStore = defineStore(
	"berriesHarvest",
	() => {
		// Filter state
		const organicStatus = ref<number | null>(null);
		const season = ref<number | null>(null);
		const date = ref<DateString>(getCurrentDateString());
		const coolers = ref<number[]>([]);
		const region = ref<number | null>(null);

		// Query data
		const { seasons: seasonsQuery } = useSeasons();
		const { coolers: coolersQuery } = useCoolers();
		const { ranches: ranchesQuery } = useHarvestOverviewRanches();
		const {
			regionOptions,
			selectedRegion,
			regionCoolerIds,
			ranchIdsInRegion,
			isLoading: isRegionDataLoading,
			hasLoadError: hasRegionDataError,
		} = useBerriesRegions(region, season);

		// Computed data
		const seasons = computed(() => seasonsQuery.value || []);
		const availableCoolers = computed(() => coolersQuery.value || []);
		const ranches = computed(() => ranchesQuery.value || []);

		// Auto-select latest season for first-time users
		watch(seasons, (newSeasons) => {
			if (
				newSeasons?.length &&
				season.value === null &&
				!localStorage.getItem("berries-harvest-filters")
			) {
				// Default to the season with the most recent start date
				const latest = [...newSeasons]
					.filter((s) => !!s?.start)
					.sort(
						(a, b) =>
							new Date(b.start as string).getTime() -
							new Date(a.start as string).getTime(),
					);
				season.value = (latest[0] ?? newSeasons[0])?.id ?? null;
			}
		});

		/**
		 * Selects a harvest region (null for all regions) and deselects the coolers outside of it,
		 * since the cooler filter only offers the selected region's coolers.
		 */
		function setRegion(regionId: number | null) {
			region.value = regionId;
			const coolerIds = regionCoolerIds.value;
			if (coolerIds) {
				coolers.value = coolers.value.filter((id) => coolerIds.has(id));
			}
		}

		function resetFilters() {
			organicStatus.value = null;
			season.value = null;
			date.value = getCurrentDateString();
			coolers.value = [];
			region.value = null;
		}

		return {
			// Filter state
			organicStatus,
			season,
			date,
			coolers,
			region,

			// Data
			seasons,
			availableCoolers,
			ranches,
			regionOptions,
			selectedRegion,
			regionCoolerIds,
			ranchIdsInRegion,
			isRegionDataLoading,
			hasRegionDataError,

			// Actions
			resetFilters,
			setRegion,
		};
	},
	{
		persist: {
			key: "berries-harvest-filters",
			pick: ["organicStatus", "season", "coolers", "region"],
		},
	},
);
